import {
  AfterViewInit,
  Component,
  ElementRef, HostListener,
  Inject,
  Injectable,
  Injector, Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {ActivatedRoute, ParamMap, Router} from "@angular/router";
import {DOCUMENT, Location} from "@angular/common";
import {cloneDeep, isArray} from 'lodash';
import {
  Components,
  createCustomFormioComponent,
  ExtendedComponentSchema, FormBuilderComponent, FormioComponent,
  FormioCustomComponentInfo,
  registerCustomFormioComponentWithClass
} from "@formio/angular";
import {DynamicFormioComponent} from "./components/dynamic-formio/dynamic-formio.component";
import {PortalComponentModel, PortalComponentStatus} from "../../core/services/portal-data/portal-component.model";
import {ToastService} from "../../core/services/toast/toast.service";
import {interval, Subscription} from "rxjs";
import {ReportDashboardModel} from "../../core/services/portal-data/report-dashboard.model";
import {PortalPageModel} from "../../core/services/portal-data/portal-page.model";
import {UserService} from "../../core/services/user/user.service";
import {LegacyNavigateService} from "../../core/services/legacy-navigate/legacy-navigate.service";
import {PortalListComponent} from "./components/portal-list/portal-list.component";
import {PortalDataService} from "../../core/services/portal-data/portal-data.service";
import {KnockoutFieldComponent} from "../../core/components/knockout-field/knockout-field.component";
import {PortalComponentParameterDatatypeModel} from "../../core/services/portal-data/portal-component-parameter-datatype.model";
import {PortalService} from "../../core/services/portal/portal.service";
import {PortalControlsComponent} from "./components/portal-controls/portal-controls.component";
import {CatalogNavigationService} from "../catalog-page/services/catalog-navigation/catalog-navigation.service";
import {PortalChartComponent} from "./components/portal-chart/portal-chart.component";
import {FeatureFlagsService} from "../../core/services/feature-flag/feature-flags.service";
import {UtilService} from "../../core/services/util/util.service";
import {DialogComponent} from "@syncfusion/ej2-angular-popups";
import {InternationalizationService} from "../../core/services/internationalization/internationalization.service";

import * as i18n from "./i18n";
import {$translate, $translateKey} from "../../core/services/internationalization/localize.service";
import {ButtonPropsModel} from "@syncfusion/ej2-popups/src/dialog/dialog-model";

const parameterDatatypeToFormioType = {
  'boolean': 'checkbox',
  'choice': 'select',
  'date': 'date', // TODO
  'date_time': 'datetime', // TODO
  'decimal': 'number',
  'integer': 'number',
  'reference': 'dynamic-sb-knockout-field',
  'string': 'textfield',
  'knockout-string': 'dynamic-sb-knockout-field',
  'knockout-choice': 'dynamic-sb-knockout-field',
};

enum DisplayType {
  PortalPage,
  ReportDashboard,
  ViewReport,
  PortalModal
}

@Injectable()
@Component({
  selector: 'sb-portal-page,sb-dynamic-portal-page',
  templateUrl: './portal-page.component.html',
  styleUrls: ['./portal-page.component.scss'],
})
export class PortalPageComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('ejDialog', {static: false}) ejDialog: DialogComponent;

  DisplayType = DisplayType; // Needed because otherwise the template cannot see it.

  // Set if we are displaying a single portal component in a modal, from a table operation button perhaps.
  @Input()
  portalModalComponent: string;

  @Input()
  portalModalComponentParams: any;

  // Set if we are displaying a portal page in a modal
  @Input()
  portalModalPage: any;

  // Options to the modal and the portal page
  @Input()
  portalModalOptions: any;

  @ViewChild('json', {static: false}) jsonElement?: ElementRef;

  @ViewChild('formBuilder', {static: false}) set setFormBuilder(formBuilder: FormBuilderComponent) {
    this.formBuilder = formBuilder;
  }

  @ViewChild('formRenderer', {static: false}) set setFormRenderer(formRenderer: FormioComponent) {
    this.formRenderer = formRenderer;
  }

  buttons: ButtonPropsModel[] = [
    {
      'click': () => { this.ejDialog.hide() },
      buttonModel: {
        content: $translate("common.button.close")`Close`,
        isPrimary: true,
      }
    },
  ];

  renderOptions: any = {
    language: 'en',
    i18n: i18n.default,
  };

  builderOptions: any = {
    // This is adjusting the left hand list of components in the form builder.
    builder: {
      premium: false,
      basic: {
        default: true,
        components: {
          textfield: false,
          textarea: false,
          number: false,
          password: false,
          checkbox: false,
          selectboxes: false,
          select: false,
          radio: false,
          button: false,
        }
      },
      advanced: {
        components: {
          email: false,
          url: false,
          phoneNumber: false,
          tags: false,
          address: false,
          datetime: false,
          day: false,
          time: false,
          currency: false,
          survey: false,
          signature: false,
        }
      },
      page: {
        title: 'Page',
        weight: 15,
        components: {}
      },
      layout: {
        components: {
          container: {
            title: 'Container',
            icon: 'folder-open',
            group: 'data',
            documentation: 'http://help.form.io/userguide/#container',
            weight: 10,
            schema: Components.components.container.schema({})
          }
        }
      },
      data: {
        components: {
          hidden: false,
          datamap: false,
          datagrid: false,
          editgrid: false,
          tree: false,
        }
      },
      report: {
        title: 'Report',
        weight: 55,
        components: {}
      },
      other: {
        title: 'Other',
        weight: 60,
        components: {}
      },
    },
    // Specify where to find the ace editor and set the default mode.
    // The ckeditor is included in common.soy as putting it here does not work.
    editors: {
      ace: {
        src: "scripts/libraries/ace.1.4.12/ace.js",
        settings: {
          mode: 'html'
        }
      }
    },
    noDefaultSubmitButton: true, // Don't make a submit button on the form by default.
    noNewEdit: false, // Don't pop up the edit window on drop. This breaks the Angular component as the onChange hooks are not called.
    language: 'en',
    i18n: i18n.default,
  };

  formBuilder: FormBuilderComponent;
  formRenderer: FormioComponent;

  builder = false;

  dashboardId: string;
  form: any = {};
  dashboardName: string;
  dashboardDescription: string;
  dashboardPrivate: boolean;
  dashboardCreatorId: string;

  dashboardCanEdit: boolean = false;
  dashboardCanCreate: boolean = false;

  displayDashboardName: boolean;
  displayDashboardDescription: boolean;

  originalForm: any;
  modifiedForm: any;
  formModified = false;

  // Dashboard refresh set from the controls component which does all components.
  dashboardRefresh: number = 0;
  refreshSubscription: Subscription;

  // Subscription to reload the portal page if the tenancy selection changes/
  tenancyReloadSubscription: Subscription;

  // Individual refresh that polls each component each second to see if it needs to be refreshed.
  individualRefreshSubscription: Subscription;

  activatedRouteSubscription: Subscription | null;

  portalComponentsLoaded$: Subscription;

  localeChanged$: Subscription;

  catalogNavigateToItemSubscription: Subscription | null;
  catalogNavigateToCategorySubscription: Subscription | null;

  debug: boolean = false;

  basePortalClasses: string[] = ["sb-portal", "sb-portal--home-page"];
  effectivePortalClasses: string[] = this.basePortalClasses;

  displayType: DisplayType;

  portalModalPages: PortalPageModel[] = null;
  portalModalPersist: boolean = false;

  // showTileFooter = true;

  pageVisible = true;
  pageHiddenStart = new Date().getTime();

  constructor(private location: Location,
              private router: Router,
              private activatedRoute: ActivatedRoute,
              private injector: Injector,
              private portalDataService: PortalDataService,
              private portalService: PortalService,
              private catalogNavigationService: CatalogNavigationService,
              private userService: UserService,
              private toastService: ToastService,
              private i18nService: InternationalizationService,
              private legacyNavigateService: LegacyNavigateService,
              private featureFlagsService: FeatureFlagsService,
              private utilService: UtilService,
              @Inject(DOCUMENT) private document: HTMLDocument) {
  }

  @HostListener('document:visibilitychange', ['$event'])
  visibilityChange() {
    // Store the current visibility state
    this.pageVisible = document.visibilityState == 'visible';

    // If the visibility state has changed from hidden to visible and the page has been hidden for more than a minute
    // then refresh those components that have a refresh setting as they might have missed some.
    if (document.visibilityState == 'visible') {
      const now = new Date().getTime();
      if ((now - this.pageHiddenStart) > 60 * 1000) {
        this.refreshAllComponents(false, 0);
      }
    } else {
      this.pageHiddenStart = new Date().getTime();
    }
  }

  ngOnInit() {

    this.renderOptions.language = this.i18nService.getBaseLanguageCode();
    this.builderOptions.language = this.i18nService.getBaseLanguageCode();

    this.debug = this.featureFlagsService.isOn("portal-debugging-display");

    // Portal modal components don't do any of this stuff.
    if (!this.portalModalComponent && !this.portalModalPage) {
      // Wipe out the page context
      (window as any).KO_PAGE_CONTEXT.clear();

      // Wipe out the Knockout content.
      const pageContentMain = document.getElementById("page-content-main");
      if (pageContentMain) {
        pageContentMain.innerHTML = "";
      }
      const errorConsole = document.getElementById("error-console");
      if (errorConsole) {
        errorConsole.innerHTML = "";
      }

      // Mark as portal page
      document.getElementById("detailMain")?.classList?.add("sb-portal-page-content");
    }

    this.portalComponentsLoaded$ = this.portalDataService.portalComponentsLoaded.subscribe(async (_) => {
      await this.refreshComponents();
    });

    this.localeChanged$ = this.i18nService.localeChanged$.subscribe(async (_) => {
      // Now done in the locale selector component
      // window.location.reload();
    });

      // Wire the catalog navigation service in so that catalog links in the portal still work
    this.catalogNavigateToCategorySubscription = this.catalogNavigationService.navigateToCategory.subscribe((id) => {
      this.router.navigateByUrl(`/Catalog/${id}`);
    });
    this.catalogNavigateToItemSubscription = this.catalogNavigationService.navigateToItem.subscribe((id) => {
      this.router.navigateByUrl(`/Catalog/${id}`);
    });

    this.individualRefreshSubscription = interval(1000).subscribe((next) => this.refreshAllComponents(false, next));

    let MessageBus = (window as any).MessageBus;

    // Subscribe to the Tenancy Filter Change event to know if we need to reload
    this.tenancyReloadSubscription = MessageBus.subscribe(
      MessageBus.CHANNELS.REFRESH_EVENT,
      'TENANCY_FILTER_CHANGE',
      (data: any, envelope: any) => {
        this.refreshAllComponents(true, this.dashboardRefresh * 1000);
      });
  }

  async refreshComponents(force: boolean = false) {
    this.modifyFormioComponentDefaultSchemas();
    this.registerCustomComponents(force);
    this.registerDynamicCustomComponents(force);

    if (this.portalModalComponent) {
      this.displayType = DisplayType.PortalModal;
      await this.displayPortalModalSyntheticPage();
    } else if (this.portalModalPage) {
      this.displayType = DisplayType.PortalModal;
      await this.displayPortalModalPage();
    } else {
      // Find the dashboard to display.
      this.activatedRouteSubscription = this.activatedRoute.paramMap.subscribe(async params => {

        if (this.activatedRoute?.snapshot?.url?.length > 0) {
          if (this.activatedRoute.snapshot.url[0].path === "ViewReport") {
            this.displayType = DisplayType.ViewReport;
            await this.displayViewReportSyntheticDashboard(params);
          } else if (this.activatedRoute.snapshot.url[0].path === "Reports") {
            this.displayType = DisplayType.ReportDashboard;
            await this.displayReportDashboard(params);
          } else {
            this.displayType = DisplayType.PortalPage;
            await this.displayPortalPage(params);
          }
        }

      });
    }

  }

  // TODO - this and displayPortalPage() are very similar, they have been separated in case their behaviour diverges.
  private async displayReportDashboard(params: ParamMap) {
    const dashboardId = params.get("id"); // 'id' might be a real id or a key

    const effectiveDefinition = await this.portalDataService.getEffectiveDefinition();

    const dashboards: ReportDashboardModel[] = this.portalDataService.getReportDashboards();

    const savedDashboardId = await this.portalDataService.getUserDashboard();

    const defaultDashboard = dashboards.find( d => d.Default.value === true);
    const dashboardById = dashboards.find(d => d.id === dashboardId);
    const dashboardByKey = dashboards.find(d => d.Key.value === dashboardId);
    const dashboardByHomePage = dashboards.find( d => d.id === effectiveDefinition?.HomePage?.record?.id);
    const savedDashboard = dashboards.find(d => d.id === savedDashboardId);
    const effectiveDashboard = dashboardById || dashboardByKey || dashboardByHomePage || savedDashboard || defaultDashboard;

    if (effectiveDashboard) {
      if (effectiveDashboard.id !== dashboardId) {
        this.portalService.navigate(effectiveDashboard.id);
      } else {
        this.portalService.navigate(effectiveDashboard.id, false);
        this.dashboardId = effectiveDashboard.id;
        this.dashboardName = $translateKey(effectiveDashboard.LocalizedName.value || effectiveDashboard.Name.value);
        this.dashboardDescription = $translateKey(effectiveDashboard.LocalizedDescription.value || effectiveDashboard.Description.value);
        this.dashboardPrivate = effectiveDashboard?.Private?.value || false;
        this.dashboardCreatorId = await this.portalDataService.getDashboardCreatorId(this.dashboardId);
        this.displayDashboardName = effectiveDashboard?.DisplayName?.value;
        this.displayDashboardDescription = effectiveDashboard?.DisplayDescription?.value;
        this.dashboardCanEdit = this.canEdit();
        this.dashboardCanCreate = this.canCreate();
        this.form = JSON.parse(effectiveDashboard.Definition.value || "{}");
        this.originalForm = cloneDeep(this.form);
        this.modifiedForm = cloneDeep(this.form);
        this.updateDebugDisplay();

        this.effectivePortalClasses = this.basePortalClasses.concat(["sb-dashboard--home-page", "sb-dashboard--top-page", "dashboard_" + effectiveDashboard.Key.value]);
      }
    }

    // Rearrange the builderOptions for report dashboard
    this.builderOptions.builder.basic = false;
    this.builderOptions.builder.advanced = false;
    this.builderOptions.builder.page = false;
    this.builderOptions.builder.data = false;
    this.builderOptions.builder.other = false;
    this.builderOptions.builder.layout.components.htmlelement = false;
    this.builderOptions.builder.layout.components.content = false;
    this.builderOptions.builder.layout.components.container = false;
    this.builderOptions.builder.layout.components.fieldset = false;

    this.portalService.registerPage(this);
  }

  // This has been changed to use the home page of the effective definition before looking at the key.
  private async displayPortalPage(params: ParamMap) {
    const dashboardId = params.get("id"); // 'id' might be a real id or a key

    const effectiveDefinition = await this.portalDataService.getEffectiveDefinition();

    const pages: PortalPageModel[] = this.portalDataService.getPages();

    const savedDashboardId = await this.portalDataService.getUserDashboard();

    const defaultDashboard = pages.find( d => d.Default.value === true);
    const dashboardById = pages.find(d => d.id === dashboardId);
    const dashboardByKey = pages.find(d => d.Key.value === dashboardId);
    const dashboardByHomePage = pages.find( d => d.id === effectiveDefinition?.HomePage?.record?.id);
    const savedDashboard = pages.find(d => d.id === savedDashboardId);
    const effectiveDashboard = dashboardById || dashboardByHomePage || dashboardByKey || savedDashboard || defaultDashboard;

    if (effectiveDashboard) {
      if (effectiveDashboard.id !== dashboardId) {
        this.portalService.navigate(effectiveDashboard.id);
      } else {
        this.portalService.navigate(effectiveDashboard.id, false);
        this.dashboardId = effectiveDashboard.id;
        this.dashboardName = effectiveDashboard.Name.value;
        this.dashboardDescription = effectiveDashboard.Description.value;
        this.form = JSON.parse(effectiveDashboard.Definition.value || "{}");
        this.originalForm = cloneDeep(this.form);
        this.modifiedForm = cloneDeep(this.form);

        this.dashboardCanEdit = this.canEdit();
        this.dashboardCanCreate = this.canCreate();

        this.updateDebugDisplay();

        this.effectivePortalClasses = this.basePortalClasses.concat(["sb-portal--top-page", "dashboard_" + effectiveDashboard.Key.value]);
      }
    }

    this.portalService.registerPage(this);
  }

  private async displayViewReportSyntheticDashboard(params: ParamMap) {
    const chartId = params.get("id");

    const viewChartForm = {
      "components": [
        {
          "columns": [
            {
              "components": [
                {
                  "hideLabel": true,
                  "type": "dynamic-sb-portal-report",
                  "properties": {
                    "Chart": chartId
                  },
                  "Chart": chartId
                }
              ],
              "width": 12,
              "offset": 0,
              "push": 0,
              "pull": 0,
              "size": "md"
            },
          ],
          "hideLabel": true,
          "type": "columns",
        }
      ]
    };

    this.dashboardId = null;
    this.dashboardName = "ViewReport";
    this.dashboardDescription = "ViewReport synthetic dashboard";
    this.form = viewChartForm;
    this.updateDebugDisplay();
  }

  private async displayPortalModalSyntheticPage() {
    const components: PortalComponentModel[] = this.portalDataService.getComponents();

    // portalModalComponent will be something like dynamic-9131c559d20911edaaa01e07799e4c95 or dynamic-ViewTemplateReport
    const dynamicPrefix = 'dynamic-';

    const componentById = components.find(d => (dynamicPrefix + d.id) === this.portalModalComponent);
    const componentByKey = components.find(d => (dynamicPrefix + d.Key) === this.portalModalComponent);
    const component = componentById || componentByKey;
    const componentId = dynamicPrefix + component?.id;

    const properties = this.portalModalComponentParams;

    const componentProperties = {...properties, ...{
        "hideLabel": true,
        "type": componentId,
        "properties": properties,
      }};

    const viewPortalModalForm = {
      "components": [
        {
          "columns": [
            {
              "components": [
                componentProperties
              ],
              "width": 12,
              "offset": 0,
              "push": 0,
              "pull": 0,
              "size": "md"
            },
          ],
          "hideLabel": true,
          "type": "columns",
        }
      ]
    };

    this.effectivePortalClasses = [];
    this.dashboardId = null;
    this.dashboardName = "PortalModalSyntheticPage";
    this.dashboardDescription = "Portal modal synthetic page";
    this.form = viewPortalModalForm;
    this.updateDebugDisplay();
  }

  private async displayPortalModalPage() {
    // These really ought to be something different as this brings in the background image from the current style, etc.
    this.effectivePortalClasses = []; //this.basePortalClasses.concat(["sb-portal--top-page", "dashboard_" + dashboardById.Key.value]);

    const pages: PortalPageModel[] = this.portalDataService.getPages();

    const dashboardById = pages.find(d => d.id === this.portalModalPage);
    const dashboardByKey = pages.find(d => d.Key === this.portalModalPage)

    const dashboard = (dashboardById || dashboardByKey);

    this.dashboardId = dashboard?.id;
    this.dashboardName = "PortalModalPage";
    this.dashboardDescription = "Portal modal page";

    if (dashboard) {
      this.form = JSON.parse(dashboard.Definition.value || "{}");
      this.updateDebugDisplay();
    }

  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy() {
    document.getElementById("detailMain")?.classList?.remove("sb-portal-page-content");

    // Remove our subscriptions.
    (this.activatedRouteSubscription) && this.activatedRouteSubscription.unsubscribe();
    (this.catalogNavigateToItemSubscription) && this.catalogNavigateToItemSubscription.unsubscribe();
    (this.catalogNavigateToCategorySubscription) && this.catalogNavigateToCategorySubscription.unsubscribe();
    this.refreshSubscription && this.refreshSubscription.unsubscribe();
    this.individualRefreshSubscription && this.individualRefreshSubscription.unsubscribe();
    this.portalComponentsLoaded$ && this.portalComponentsLoaded$.unsubscribe();
    this.localeChanged$ && this.localeChanged$.unsubscribe();
    this.tenancyReloadSubscription && this.tenancyReloadSubscription.unsubscribe();

    this.portalService.deregisterPage(this);
  }

  onChange(event: any) {
    this.formModified = true;
    this.modifiedForm = cloneDeep(event.form);
    this.updateDebugDisplay();
  }

  // TODO Attempt to make rendering smoother - doesn't work very well.
  formLoad(event: any) {
    if (event.components) {
      const wrapper = document.querySelector('#sb-portal-formio-wrapper') as HTMLElement;
      if (wrapper) {
        wrapper.style.setProperty('display', 'none');
      }
    }
  }

  // TODO Attempt to make rendering smoother - doesn't work very well as formReady seems to be called twice when a page is first visited.
  formReady(event: any) {
    this.utilService.tickThen(() => {
      const wrapper = document.querySelector('#sb-portal-formio-wrapper') as HTMLElement;
      if (wrapper) {
        wrapper.style.removeProperty('display');
      }
    });
  }

  /**
   * Allows us to react to certain Formio events from a Generic perspective.
   *
   * @param event The Formio Event
   */
  formioOnChange(event: any) {
    let componentType = event?.changed?.component?.type;
    let isTab = componentType === "tabs";

    if (isTab) {
      // Send a delayed resize event so List Views and Chares can render if required
      setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
    }
  }

  isAdministrator() {
    return this.userService.isAdministrator();
  }

  canGoBack() {
    return this.legacyNavigateService.canGoBack();
  }

  back() {
    this.legacyNavigateService.back();
  }

  cancel() {
    this.form = cloneDeep(this.originalForm);
    this.builder = false;
    this.formModified = false;
  }

  canEdit(): boolean {
    const isReportEditor = this.userService.hasRole("report_editor");
    const isAdmin = this.isAdministrator();
    const isCreator = this.dashboardCreatorId === this.userService.getID();
    if (this.dashboardPrivate) {
      return isCreator || isAdmin;
    } else {
      return isReportEditor || isAdmin;
    }
  }

  canCreate(): boolean {
    const isReportEditor = this.userService.hasRole("report_editor");
    const isAdmin = this.isAdministrator();
    const isCreator = this.dashboardCreatorId === this.userService.getID();
    if (this.dashboardPrivate) {
      return isCreator || isAdmin;
    } else {
      return isReportEditor || isAdmin;
    }
  }

  // AKA Save & back
  async save() {
    await this.update();
    this.builder = false;
    if (this.canGoBack()) {
      this.back();
    }
  }

  // AKA Save & stay
  async update() {
    const id = this.dashboardId;
    const definition = JSON.stringify(this.modifiedForm);
    const result = await this.portalDataService.saveDashboard(id, definition);

    // Once you have saved it you can't go back to the original.
    this.originalForm = cloneDeep(this.modifiedForm);
    this.form = cloneDeep(this.modifiedForm);

    this.toastService.showSuccess("Saved successfully");
  }

  preview() {
    this.builder = false;
    this.form = cloneDeep(this.modifiedForm);
  }

  edit() {
    this.builder = true;
    this.form = cloneDeep(this.modifiedForm);
  }

  displayPageModal(persist: boolean = false) {
    this.portalModalPages = this.portalDataService.getPages();
    this.portalModalPersist = persist;
    this.utilService.tickThen(() => {
      this.ejDialog.show();
    });
  }

  onClose($event: any) {
    this.ejDialog.hide();
    this.portalModalPages = null;
    this.portalModalPersist = false;
  }

  buttonLabel(key: string): string {
    return this.i18nService.getMessage(key);
  }

  /**
   * This is the dashboard refresh set in the controls component
   */
  setRefresh(seconds: number) {
    this.dashboardRefresh = seconds;
    this.refreshSubscription?.unsubscribe();
    if (this.dashboardRefresh > 0) {
      this.refreshSubscription = interval(this.dashboardRefresh * 1000).subscribe((next) => this.refreshAllComponents(true, this.dashboardRefresh * next));
    }
  }

  refreshAllComponents(dashboardOverride: boolean, tick: number) {
    if (this.pageVisible) {
      if (this.portalService) {
        const comps = this.portalService.getComponentsForPage(this);

        comps.forEach((c) => {
          const componentRefresh: number = c.formioComponent.component?._refresh;
          const componentDashboardRefreshOverride: boolean = c.formioComponent.component?._dashboardRefreshOverride;

          let doRefresh = false;
          // Dashboard override does all components unless they forbid it.
          if (dashboardOverride && componentDashboardRefreshOverride) {
            doRefresh = true;
          }
          // Individual refresh only does those components that allow refresh and have met their period requirement
          if (!dashboardOverride && componentRefresh > 0 && tick % componentRefresh === 0) {
            doRefresh = true;
          }

          if (doRefresh) {
            c.refresh();
          }
        });
      }
    }
  }

  async selectDashboard(dashboardId: string, persist: boolean = false) {
    if (this.dashboardId !== dashboardId) {
      if (persist) {
        await this.portalDataService.updateUserDashboard(dashboardId);
      }
      this.portalService.navigate(dashboardId);
    }
  }

  debugStyle() {
    return this.debug ? '' : 'display: none';
  }

  private updateDebugDisplay() {
    if (this.jsonElement) {
      this.jsonElement.nativeElement.innerHTML = '';
      this.jsonElement.nativeElement.appendChild(document.createTextNode(JSON.stringify(this.modifiedForm, null, 4)));
    }
  }

  // This modifies the Formio defaults for their predefined components, `builderInfo` is what is displayed in
  // the left hand component chooser (such as title), while `schema` is the default for the component itself (such as defaultValue)
  private modifyFormioComponentDefaults(type: string, builderInfo: object = {}, schema: object = {}) {
    return Object.assign({},
      (Components.components[type] as any).builderInfo,
      builderInfo,
      {
        schema: Object.assign({}, Components.components[type].schema({}), schema)
      },
    )
  }

  // Modify the schema of predefined Formio components. Necessary as the build info tree is not always used
  // to supply all defaults as the defaultSchema method on many components is also used which does not involve
  // the build info tree.
  private modifyFormioComponentDefaultSchemas() {

    const marker = "__servicely_schema_modified";

    const componentsToModify = ['htmlelement', 'content', 'container', 'columns', 'fieldset', 'panel', 'table', 'tabs', 'well'];

    componentsToModify.forEach((name) => {

      const component = Components.components[name];

      if (!component[marker] && !name.startsWith("dynamic-")) {

        // Modify 'columns' schema - special case to allow a single column.
        if (name === "columns") {
          const originalSchemaFn = component.schema;
          component.schema = (...extend) => {
            return Object.assign({}, originalSchemaFn(extend), {
              columns: [{components: [], width: 12, offset: 0, push: 0, pull: 0, size: 'md'}],
            });
          }
        }

        const originalEditFormFn = component.editForm;
        component.editForm = (...extend) => {

          const editForm = originalEditFormFn(...extend);

          // Hide or remove all the things we don't want at the moment.
          // We hide the tabs: api, conditional, logic, layout, data, validation
          // We hide most of the fields on the tab 'display'
          const tabsToHide = ['api', 'conditional', 'logic', 'layout', 'data', 'validation'];
          const commonDisplayFieldsToKeep = ['label', 'hideLabel', 'labelPosition', 'legend', 'theme', 'customClass'];
          const specificDisplayFieldsToKeep = {
            columns: ['columns'],
            htmlelement: ['tag', 'className', 'attrs', 'content'],
            table: ['numRows', 'numCols', 'cellAlignment'],
            tabs: ['components', 'dataGridLabel'],
            panel: ['title']
          };

          const tabs = editForm.components.filter((x) => x.type === 'tabs')[0].components;
          tabs.forEach((x) => {
            if (tabsToHide.includes(x.key)) {
              Object.keys(x).forEach((key) => key === 'key' || delete x[key]);
              x.ignore = true;
            }
          });
          const display = tabs.find((x) => x.key === 'display').components;

          const componentsToKeep = display.reduce((acc, x) => {
            if (commonDisplayFieldsToKeep.includes(x.key) || (specificDisplayFieldsToKeep[name] && specificDisplayFieldsToKeep[name].includes(x.key))) {
              acc.push(x);
            }
            return acc;
          }, []);

          display.length = 0;
          display.push(...componentsToKeep);

          return editForm;
        }

        component[marker] = true;
      }
    });
  }

  // These components are implemented in the code rather than being read from the database.
  private registerCustomComponents(force: boolean = false) {

    this.register({
      id: 'sb-knockout-field',
      Name: 'Knockout field',
      Description: 'A form field rendered with Knockout',
      params: [],
      fieldOptions: ['field'], // Custom fields in this component that can be set in the edit form
      componentGroup: 'hidden', // A group not in the list of groups in the form builder, hence they are hidden.
      componentClass: KnockoutFieldComponent,
      componentStatus: PortalComponentStatus.Existing,
    }, force);

    this.register({
      id: 'sb-portal-controls',
      Name: 'Control strip',
      Description: 'Control strip for a page',
      params: [],
      componentGroup: 'other',
      componentClass: PortalControlsComponent,
      componentStatus: PortalComponentStatus.Existing,
    }, force);

    this.register({
      id: 'sb-portal-public-report',
      Name: 'Public Reports',
      Description: 'A report',
      params: [
        {
          id: null,
          Name: 'Chart',
          Key: 'Chart',
          Description: 'Public report which the component displays',
          Datatype: PortalComponentParameterDatatypeModel.Reference,
          ReferenceTable: 'Report',
          AvailableValues: null,
          Default: null,
          rendererProperties: {
            additionalFilterQuery: {operator: "and", value: [
                {operator: "=", field: "Private", value: false}
              ]}
          },
        },
      ],
      componentGroup: 'report',
      componentClass: PortalChartComponent,
      componentStatus: PortalComponentStatus.Existing,
    }, force);

    this.register({
      id: 'sb-portal-report',
      Name: 'My Reports',
      Description: 'A report',
      params: [
        {
          id: null,
          Name: 'Chart',
          Key: 'Chart',
          Description: 'Private report which the component displays',
          Datatype: PortalComponentParameterDatatypeModel.Reference,
          ReferenceTable: 'Report',
          AvailableValues: null,
          Default: null,
          rendererProperties: {
            additionalFilterQuery: {operator: "and", value: [
                {operator: "=", field: "CreatedBy", value: this.userService.getID()},
                {operator: "=", field: "Private", value: true}
              ]}
          },
        },
      ],
      componentGroup: 'report',
      componentClass: PortalChartComponent,
      componentStatus: PortalComponentStatus.Existing,
    }, force);

    this.registerDynamicPortalListComponent(force);
  }

  private registerDynamicPortalListComponent(force: boolean = false) {
    let tables: { value: string, i18nKey: string }[] = this.portalDataService.getTables();

    const tableAvailableValues = tables.reduce((acc, curr) => {
      acc[curr.value] = {i18nKey: curr.i18nKey, i18nValue: curr.value};
      return acc;
    }, {});

    // This observable and the valueChangeCallback are a hack to make the Query dependent on the Table. Some changes
    // in field_renderer.js:2639 were also needed for the condition renderer.
    const tableObservable = (window as any).KO.observable('User');

    // The Columns and SortOrder fields are also dependent on the Table field but their available values
    // are updated in a different way.

    let columnsField = null;
    let sortOrderField = null;

    const columnsAvailableValues = {};
    const sortOrderAvailableValues = {};

    let systemFieldI18nKey = {
      id: 'core.id.name',
      SID: 'core.sid.name',
      CreatedOn: 'core.createdon.name',
      CreatedBy: 'core.createdby.name',
      UpdatedOn: 'core.updatedon.name',
      UpdatedBy: 'core.updatedby.name',
      Version: 'core.version.name',
      TableClass: 'core.tableclass.name',
      Application: 'core.application.name',
    };

    const updateColumnsField = (tableName: string, init: boolean) => {

      if (!columnsField) {
        return;
      }

      const tableDefinition = (window as any).TableManager.getTableDefinition(tableName);

      let fields = tableDefinition.getFields();

      let av = Object.values(fields).map((f: any) => {
        let key = systemFieldI18nKey[f.getName()] || f.getI18nKey() || f.getName();
        return {
          value: f.getName(),
          i18nKey: (window as any).I18n.getMessage(key)
        };
      });

      if (!init) {
        columnsField.value([]);
      }
      columnsField.availableValues(av);
    };

    const updateSortOrderField = (tableName: string, init: boolean) => {

      if (!sortOrderField) {
        return;
      }

      const tableDefinition = (window as any).TableManager.getTableDefinition(tableName);

      let fields = tableDefinition.getFields();
      let value = (f, asc) => {
        let suffix = asc ? ':asc' : ':desc';
        return f.getName() + suffix;
      }
      let message = (f, asc) => {
        let key = systemFieldI18nKey[f.getName()] || f.getI18nKey() || f.getName();
        let msg = (window as any).I18n.getMessage(key);
        let classes = asc ? 'mi mi_arrow_upward' : 'mi mi_arrow_downward';
        return msg + '&nbsp;<i class="' + classes + '"></i>';
      };

      let values = Object.values(fields);

      let avAsc = values.map((f) => {
        return {
          value: value(f, true),
          i18nKey: message(f, true)
        };
      });
      let avDesc = values.map((f) => {
        return {
          value: value(f, false),
          i18nKey: message(f, false)
        };
      });

      let av = [
        ...avAsc,
        {
          value: '---',
          i18nKey: '<i class="mi">remove</i><i class="mi">remove</i><i class="mi">remove</i><i class="mi">remove</i>',
          disabled: true,
        },
        ...avDesc];

      if (!init) {
        sortOrderField.value([]);
      }
      sortOrderField.availableValues(av);
    };

    this.register({
      id: 'sb-portal-list',
      Name: 'Filtered list',
      Description: 'A filtered list of table items',
      params: [
        {
          id: null,
          Name: 'Table',
          Key: 'Table',
          Description: 'Table which the list displays',
          Datatype: PortalComponentParameterDatatypeModel.KnockoutChoice,
          ReferenceTable: null,
          AvailableValues: tableAvailableValues,
          Default: null,
          valueChangeCallback: (value) => {
            tableObservable(value);
            updateColumnsField(tableObservable(), false);
            updateSortOrderField(tableObservable(), false);
          }
        },
        {
          id: null,
          Name: 'Query',
          Key: 'Query',
          Description: 'Query which filters the list',
          Datatype: PortalComponentParameterDatatypeModel.KnockoutString,
          ReferenceTable: null,
          AvailableValues: {},
          Default: null,
          renderer: 'condition',
          rendererProperties: {
            'portal.table.observable': tableObservable,
          },
        },
        {
          id: null,
          Name: 'Columns',
          Key: 'Columns',
          Description: 'Columns which the list displays',
          Datatype: PortalComponentParameterDatatypeModel.KnockoutChoice,
          ReferenceTable: null,
          AvailableValues: columnsAvailableValues,
          Default: null,
          renderer: 'multichoice',
          rendererProperties: {
            "sortable": "true",
          },
          fieldCreationCallback: (data: any) => {
            columnsField = data.field;
            updateColumnsField(tableObservable(), true);
          },
          serializeValue: true,
        },
        {
          id: null,
          Name: 'Sort order',
          Key: 'SortOrder',
          Description: 'Sort order which the list displays',
          Datatype: PortalComponentParameterDatatypeModel.KnockoutChoice,
          ReferenceTable: null,
          AvailableValues: sortOrderAvailableValues,
          Default: null,
          renderer: 'multichoice',
          rendererProperties: {
            "sortable": "true",
          },
          fieldCreationCallback: (data: any) => {
            sortOrderField = data.field;
            updateSortOrderField(tableObservable(), true);
          },
          serializeValue: true,
        },
      ],
      componentGroup: 'report',
      componentClass: PortalListComponent,
      componentStatus: PortalComponentStatus.Existing,
    }, force);
  }

  private registerDynamicCustomComponents(force: boolean = false) {
    let components: PortalComponentModel[] = this.portalDataService.getComponents();

    components = cloneDeep(components);

    (components as any[]).forEach( (comp: PortalComponentModel) => {
      comp.componentGroup = comp.Category || 'Other';
      comp.componentClass = DynamicFormioComponent;
    });

    (components as any[]).forEach( (comp: PortalComponentModel) => {
      this.register(comp, force);
    });
  }

  private register(comp: PortalComponentModel, force: boolean) {

    const componentType = `dynamic-${comp.id}`;

    // Check if this component already exists, because it can't be registered twice.

    if (force) {
      console.debug(`PortalPageComponent: component ${componentType} '${comp.Name}' has status '${comp.componentStatus}', force reload cannot work with customElements`);
    }

    if (comp.componentStatus === PortalComponentStatus.Existing) {
      if (customElements.get(componentType)) {
        // We think it is existing, and in fact it does exist, so don't add again. Otherwise we'll add it.
        return;
      }
    } else if (comp.componentStatus === PortalComponentStatus.Updated) {
      if (customElements.get(componentType)) {
        // We think it is being updated and therefore it exists, and in fact it does exist so we warn about it and don't update it.
        console.debug(`PortalPageComponent: component ${componentType} '${comp.Name}' has status '${comp.componentStatus}', reload the page to make it available`);
        return;
      } else {
        console.debug(`PortalPageComponent: component ${componentType} '${comp.Name}' has status '${comp.componentStatus}', but customElements thinks not. Continuing.`);
      }
    } else if (comp.componentStatus === PortalComponentStatus.New) {
      if (customElements.get(componentType)) {
        console.debug(`PortalPageComponent: component ${componentType} '${comp.Name}' has status '${comp.componentStatus}', but customElements already has it. Ignoring.`);
        return;
      }
    }


    interface ParamDefinition {
      type?: string;
      label?: string;
      key?: string;
      tooltip?: string;
      defaultValue?: any;
      input?: boolean;
      dataSrc?: string;
      template?: string;
      data?: any[];
    }

    const params: ParamDefinition[] = comp.params.map (param => {
      const name = param.Name;
      const key = param.Key;
      const description = param.Description;
      const datatype = param.Datatype;
      const defaultValue = param.Default;
      const referenceTable = param.ReferenceTable;
      const renderer = param.renderer;
      const rendererProperties = param.rendererProperties;
      const valueChangeCallback = param.valueChangeCallback;
      const fieldCreationCallback = param.fieldCreationCallback;
      const serializeValue = param.serializeValue != null ? param.serializeValue : false;

      let paramDesc: any = {
        type: parameterDatatypeToFormioType[datatype],
        label: name,
        key: key,
        tooltip: description,
        defaultValue: defaultValue,
        input: true,
        serializeValue: serializeValue,
      };

      // Manipulate the availableValues to put it into a form that formio 'select' field wants.
      if (datatype === "choice") {
        const hasOrder = Object.values(param.AvailableValues).filter((av) => av.order).length > 0;

        let availableValues = [];

        if (hasOrder) {
          availableValues = Object.entries(param.AvailableValues)
            .sort((a, b) => Number(a[1].order || '10000') - Number(b[1].order || '10000'))
            .map((p) => {
              return {value: p[0], label: p[1].i18nValue}
            });
        } else {
            // Sort alphabetically by i18nValue
          availableValues = Object.entries(param.AvailableValues)
            .sort((a, b) => (a[1].i18nValue > b[1].i18nValue) ? 1 : -1)
            .map((p) => {
              return {value: p[0], label: p[1].i18nValue}
            });
        }

        paramDesc = {...paramDesc, ...{
            dataSrc: 'values',
            template: '<span>{{ item.label }}</span>',
            data: {
              values: availableValues
            }
          }
        };
      }

      // Define the 'field' that the KnockoutFieldComponent wants.
      if (datatype === "reference") {
        paramDesc = {...paramDesc, ...{
            field: {
              name: referenceTable,
              i18nKey: name,
              datatype: 'reference',
              availableValues: [],
              referenceTable: referenceTable,
              question: null,
              readonly: false,
              mandatory: false,
              enablePreview: false,
              tooltip: null,
              data: {
                defaultValue: null, // null | {value?: string, displayValue?: string };
                defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
                answer: {
                  value: null,
                  displayValue: null
                }, // null | {value?: string, displayValue?: string };
              },
              changed: null, // EventEmitter<KnockoutFieldModel>;
              requestId: null,
              table: 'SyntheticTable',
              renderer: renderer,
              rendererProperties: rendererProperties,
              valueChangeCallback: valueChangeCallback,
              fieldCreationCallback: fieldCreationCallback,
            }
          }
        };
      }

      // Define the 'field' that the KnockoutFieldComponent wants.
      if (datatype === "knockout-string") {
        paramDesc = {...paramDesc, ...{
            field: {
              name: 'String',
              i18nKey: name,
              datatype: 'string',
              availableValues: [],
              referenceTable: null,
              question: null,
              readonly: false,
              mandatory: false,
              enablePreview: false,
              tooltip: null,
              data: {
                defaultValue: null, // null | {value?: string, displayValue?: string };
                defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
                answer: {
                  value: null,
                  displayValue: null
                }, // null | {value?: string, displayValue?: string };
              },
              changed: null, // EventEmitter<KnockoutFieldModel>;
              requestId: null,
              table: 'SyntheticTable',
              renderer: renderer,
              rendererProperties: rendererProperties,
              valueChangeCallback: valueChangeCallback,
              fieldCreationCallback: fieldCreationCallback,
            }
          }
        };
      }

      // Define the 'field' that the KnockoutFieldComponent wants.
      if (datatype === "knockout-choice") {

        const avForKO = Object.entries(param.AvailableValues)
                              .map((e) => { return {value: e[0], i18nKey: e[1].i18nKey} })
                              .sort((a, b) =>  (a.value > b.value) ? 1 : -1);

        paramDesc = {...paramDesc, ...{
            field: {
              name: 'Choice',
              i18nKey: name,
              datatype: 'choice',
              availableValues: avForKO,
              referenceTable: null,
              question: null,
              readonly: false,
              mandatory: false,
              enablePreview: false,
              tooltip: null,
              data: {
                defaultValue: null, // null | {value?: string, displayValue?: string };
                defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
                answer: {
                  value: null,
                  displayValue: null
                }, // null | {value?: string, displayValue?: string };
              },
              changed: null, // EventEmitter<KnockoutFieldModel>;
              requestId: null,
              table: 'SyntheticTable',
              renderer: renderer,
              rendererProperties: rendererProperties,
              valueChangeCallback: valueChangeCallback,
              fieldCreationCallback: fieldCreationCallback,
            }
          }
        };
      }

      return paramDesc;
    });

    const fieldOptions = comp.fieldOptions || params.map((p) => p.key);

    const schemaParams = (params as any[]).reduce((obj, param) => {
      obj[param.key] = param.defaultValue;
      return obj;
    }, {});

    let componentInfo: FormioCustomComponentInfo = {

      type: componentType, // key into map of components
      selector: componentType, // Angular Elements selector
      title: comp.Name,
      group: comp.componentGroup, // Group in the left hand nav.
      icon: 'puzzle-piece',

      // The component edit form is based on the 'textfield' one but modified.
      editForm: () => {
        return (Components.components.textfield.editForm as any)([
          {
            key: 'parameters',
            label: 'Parameters',
            components: params,
            weight: -10, // The weight of the 'display' tab is 0 so we need to be negative to be before.
          },
          {
            key: 'refresh',
            label: 'Refresh',
            components: [
              {
                key: '_refresh',
                label: 'Refresh period',
                type: 'select',
                dataSrc: "values",
                data: {
                  "values": [
                    {
                      "label": "Off",
                      "value": "0"
                    },
                    {
                      "label": "1 minute",
                      "value": "60"
                    },
                    {
                      "label": "2 minutes",
                      "value": "120"
                    },
                    {
                      "label": "5 minutes",
                      "value": "300"
                    },
                    {
                      "label": "15 minutes",
                      "value": "900"
                    }
                  ],
                },
                defaultValue: "0"
              },
              {
                key: '_dashboardRefreshOverride',
                label: 'Dashboard refresh overrides component',
                type: 'checkbox',
                defaultValue: true
              }
            ],
            weight: -5, // The weight of the 'display' tab is 0 so we need to be negative to be before.
          },
          {
            key: 'display',
            components: [
              {
                key: 'label',
                validate: {
                  required: false // This still runs the required validation for some reason.
                }
              },
              {
                key: 'prefix',
                ignore: true
              },
              {
                key: 'suffix',
                ignore: true
              },
              {
                key: 'inputMask',
                ignore: true
              },
              {
                key: 'customClass',
                ignore: true
              },
              {
                key: 'allowMultipleMasks',
                ignore: true
              },
              {
                key: 'allowMultipleMasks',
                ignore: true
              },
              {
                key: 'tabindex',
                ignore: true
              },
              {
                key: 'showWordCount',
                ignore: true,
              },
              {
                key: 'showCharCount',
                ignore: true
              },
              {
                key: 'mask',
                ignore: true
              },
              {
                key: 'autofocus',
                ignore: true
              },
              {
                key: 'hidden',
                ignore: true
              },
              {
                key: 'hideLabel',
                defaultValue: true, // The value in schema below will override this
              },
              {
                key: 'modalEdit',
                ignore: true
              },
              {
                key: 'spellcheck',
                ignore: true,
              },
              {
                key: 'disabled',
                ignore: true
              },
              {
                key: 'tableView',
                ignore: true
              },
              {
                key: 'placeholder',
                ignore: true
              },
              {
                key: 'tooltip',
                ignore: true
              },
              {
                key: 'description',
                ignore: true
              },
      ]
          },
          {
            key: 'data',
            ignore: true
          },
          {
            key: 'validation',
            ignore: true
          },
          {
            key: 'api',
            ignore: true
          },
          {
            key: 'logic',
            ignore: true
          },
          {
            key: 'layout',
            ignore: true
          },
          {
            key: 'conditional',
            ignore: true
          },
        ])
      },
      // Values in 'schema' override those in the edit form.
      schema: { // ExtendedComponentSchema = ComponentSchema<T> & { [key: string]: any }, becomes CustomComponent.schema()
        type: componentType,
        label: comp.Name,
        documentation: '', // URL to help page
        input: false,
        tooltip: comp.Description,
        hideLabel: true,
        ...schemaParams
      },
      fieldOptions: fieldOptions
    };

    const BaseCustomComponent = createCustomFormioComponent(componentInfo);

    // Enhance the standard custom component with one that sets all the parameters into one field
    class EnhancedCustomComponent extends BaseCustomComponent {
      constructor(public component: ExtendedComponentSchema, options: any, data: any) {
        super(component, options, data);
      }
      attach(element: HTMLElement) {
        super.attach(element);

        // Set all the parameters into one field
        const fieldOptions = componentInfo.fieldOptions;
        if (isArray(fieldOptions) && fieldOptions.length > 0) {
          const parameters = {};
          for (const key in fieldOptions) {
            if (fieldOptions.hasOwnProperty(key)) {
              parameters[fieldOptions[key]] = this.component[fieldOptions[key]];
            }
          }
          this._customAngularElement['parameters'] = parameters;
          // Angular change detection only works properly for primitive types.
          this._customAngularElement['parametersJSON'] = JSON.stringify(parameters);
        }

        // Detect if this is rendering in a preview or in the list of parameters of an edit dialog
        let isPreview = false;
        for (let e = element; e; e = e.parentElement) {
          if (e.classList.contains("component-preview") || e.classList.contains("component-settings")) {
            isPreview = true;
            break;
          }
        }
        this._customAngularElement['preview'] = isPreview;

        this._customAngularElement['serializeValue'] = this.component.serializeValue;

        // Make the formio component accessible from the angular component
        this._customAngularElement['formioComponent'] = this;
      }
    }

    registerCustomFormioComponentWithClass(componentInfo, comp.componentClass, EnhancedCustomComponent, this.injector);
  }

  // showHideFooter() {
  //   this.showTileFooter = !this.showTileFooter;
  // }
}
